import { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Select, DatePicker, Table } from 'antd';
import Chart from 'react-apexcharts';
import { Button, Loading, Icon } from '~/components';
import { reportApi } from '~/api';
import { formatCurrency, handleRequest, apiError } from '~/utils';
import dayjs from 'dayjs';
import '../style/Report.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

function RevenueReport()
{
    const [loading, setLoading] = useState(false);

    const [timeRange, setTimeRange] = useState('today');

    const [customDates, setCustomDates] = useState([]);

    const [groupBy, setGroupBy] = useState('day'); // day, month, year

    const [data, setData] = useState(null);

    const loadReport = useCallback(async () =>
    {
        setLoading(true);

        const params = {
            type: timeRange,
            group_by: groupBy
        };

        if (timeRange === 'custom' && customDates.length === 2) {
            params.start_date = customDates[0].format('YYYY-MM-DD');
            params.end_date = customDates[1].format('YYYY-MM-DD');
        }

        const [error, response] = await handleRequest(reportApi.revenue(params));

        setLoading(false);

        if (!apiError('Lỗi khi tải báo cáo', error, response)) {
            setData(response.data);
        }
    }, [timeRange, customDates, groupBy]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    const columns = [
        {
            title: groupBy === 'day' ? 'Ngày' : groupBy === 'month' ? 'Tháng' : 'Năm',
            dataIndex: groupBy === 'day' ? 'date' : 'period',
            key: 'period',
            render: (value, record) => {
                if (groupBy === 'day') {
                    return dayjs(record.date).format('DD/MM/YYYY');
                } else if (groupBy === 'month') {
                    return `Tháng ${record.month}/${record.year}`;
                } else {
                    return `Năm ${record.year}`;
                }
            },
        },
        {
            title: 'Số đơn hàng',
            dataIndex: 'orders_count',
            key: 'orders_count',
            align: 'right',
        },
        {
            title: 'Doanh thu (chưa VAT)',
            dataIndex: 'revenue',
            key: 'revenue',
            align: 'right',
            render: (value) => formatCurrency(value),
        },
        {
            title: 'Thuế',
            dataIndex: 'vat',
            key: 'vat',
            align: 'right',
            render: (value) => formatCurrency(value),
        },
        {
            title: 'Doanh thu (có VAT)',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            render: (value) => formatCurrency(value),
        },
    ];

    // Prepare chart data for ApexCharts - Area chart with gradient
    const chartOptions = {
        chart: {
            type: 'area',
            height: 350,
            toolbar: {
                show: false
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800
            },
            dropShadow: {
                enabled: true,
                top: 0,
                left: 0,
                blur: 3,
                opacity: 0.1
            }
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        colors: ['#2E93fA', '#FF6178', '#66DA26'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.2,
                stops: [0, 90, 100]
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: data?.by_date?.map(item => {
                if (groupBy === 'day') {
                    return dayjs(item.date || item.period).format('DD/MM');
                } else if (groupBy === 'month') {
                    return `T${item.month}`;
                } else {
                    return item.year;
                }
            }) || [],
            labels: {
                style: {
                    colors: '#8e8da4'
                }
            },
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => `${parseInt(value / 1000)}K`,
                style: {
                    colors: '#8e8da4'
                }
            }
        },
        tooltip: {
            y: {
                formatter: (value) => formatCurrency(value)
            }
        },
        grid: {
            borderColor: '#e7e7e7',
            strokeDashArray: 5,
            yaxis: {
                lines: {
                    show: true
                }
            }
        },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'left',
            fontSize: '12px',
            fontFamily: 'inherit',
            labels: {
                colors: '#8e8da4'
            }
        }
    };

    // Calculate total revenue and profit
    const totalRevenue = data?.summary?.total_revenue || 0;

    const totalOrder = data?.summary?.total_orders || 0;

    const totalVat = data?.summary?.total_vat || 0;

    const total = data?.summary?.total || 0;

    const chartSeries = [
        {
            name: 'Doanh thu',
            data: data?.by_date?.map(item => parseInt(item.revenue)) || []
        },
        {
            name: 'Thuế',
            data: data?.by_date?.map(item => parseInt(item.vat)) || []
        },
        {
            name: 'Đơn hàng',
            data: data?.by_date?.map(item => parseInt(item.orders_count)) || []
        }
    ];

    return (
        <div className="container">
            <div className="report-container">
                <div className="report-header">
                    <h1>Báo cáo Doanh thu</h1>
                    <div className="report-filters">
                        <Select
                            value={timeRange}
                            onChange={setTimeRange}
                            style={{ width: 200 }}
                        >
                            <Option value="today">Hôm nay</Option>
                            <Option value="yesterday">Hôm qua</Option>
                            <Option value="this_week">Tuần này</Option>
                            <Option value="last_week">Tuần trước</Option>
                            <Option value="this_month">Tháng này</Option>
                            <Option value="last_month">Tháng trước</Option>
                            <Option value="this_year">Năm nay</Option>
                            <Option value="custom">Tùy chọn</Option>
                        </Select>

                        {timeRange === 'custom' && (
                            <RangePicker
                                value={customDates}
                                onChange={setCustomDates}
                                format="DD/MM/YYYY"
                                style={{ marginLeft: 10 }}
                            />
                        )}

                        <Select
                            value={groupBy}
                            onChange={setGroupBy}
                            style={{ width: 150, marginLeft: 10 }}
                        >
                            <Option value="day">Theo ngày</Option>
                            <Option value="month">Theo tháng</Option>
                            <Option value="year">Theo năm</Option>
                        </Select>

                        <Button
                            primary
                            onClick={loadReport}
                            style={{ marginLeft: 10 }}
                            disabled={timeRange === 'custom' && customDates.length !== 2}
                        >
                            Xem báo cáo
                        </Button>
                    </div>
                </div>

                {loading && <Loading />}

                {!loading && data && (
                    <>
                        <Row gutter={16} className="report-summary">
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-cost">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper cost-icon">
                                            <span className="icon-text">₫</span>
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Đơn hàng</div>
                                        <div className="stat-value">{totalOrder}</div>
                                        <div className="stat-trend negative">Tổng đơn hàng bán được</div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-revenue">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper revenue-icon">
                                            <span className="icon-text">₫</span>
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Doanh thu</div>
                                        <div className="stat-value">{formatCurrency(totalRevenue)}</div>
                                        <div className="stat-trend positive">Doanh thu chưa bao gồm thuế</div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-profit">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper profit-icon">
                                            {Icon.chartLine}
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Thuế</div>
                                        <div className="stat-value">{formatCurrency(totalVat)}</div>
                                        <div className="stat-trend positive">Tổng chi phí thuế</div>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Card className="stat-card stat-card-margin">
                                    <div className="stat-icon">
                                        <div className="icon-wrapper margin-icon">
                                            <span className="icon-text">%</span>
                                        </div>
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-title">Tổng</div>
                                        <div className="stat-value">{formatCurrency(total)}</div>
                                        <div className="stat-trend">Tổng doanh thu</div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        <Card title="Biểu đồ Doanh thu - Chi phí - Lợi nhuận" className="chart-card">
                            <Chart
                                options={chartOptions}
                                series={chartSeries}
                                type="area"
                                height={350}
                            />
                        </Card>

                        <Card title={`Chi tiết theo ${groupBy === 'day' ? 'ngày' : groupBy === 'month' ? 'tháng' : 'năm'}`} className="table-card">
                            <Table
                                columns={columns}
                                dataSource={data.by_date}
                                rowKey={(record) => record.period || record.date}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) => `Tổng ${total} ${groupBy === 'day' ? 'ngày' : groupBy === 'month' ? 'tháng' : 'năm'}`
                                }}
                            />
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}

export default RevenueReport;
